import { CheckboxGroup } from '@eventbrite/eds-checkbox-group';
import { $FixMe } from '@eventbrite/ts-utils';
import React, { useEffect } from 'react';
import './FilterCheckboxGroup.scss';

interface FilterCheckboxGroupPops {
    /*
     * Name of the checkbox group. Required
     */
    name: string;
    /*
     * List of all possible values this checkbox
     * group should render.
     */
    values: {
        //Support object due to gettext
        display?: object | string;
        value?: string;
    }[];
    /*
     * The values of the checkboxes which are currently selected. In some cases
     * will be passed a string representation which it handles by converting
     * into an array.
     */
    selectedFilters?: string | any[];
    /*
     * Boolean dictating if mobile screen or not
     */
    isMobile?: boolean;
    /*
     * Callback for when a checkbox is clicked
     */
    onClick?: Function;
}

const FilterCheckboxGroup = (props: FilterCheckboxGroupPops) => {
    const elementId = `filter-checkbox-group__${props.name}--${
        props.isMobile ? 'mobile' : 'desktop'
    }`;

    /*
    Queries dom for active checkboxes and
    assigns "active" classes to the
    checkbox wrapping components.

    Due to no way via CSS to select
    a parent, we need to leverage this approach
    in order to properly update the styling of
    the checkbox's parent element.
    */
    const setActiveClasses = React.useCallback(() => {
        const applyClasses = (checkboxes: Element[], isActive?: boolean) =>
            checkboxes.forEach((checkbox) => {
                const action = isActive ? 'add' : 'remove';
                const parent = checkbox.parentNode;
                const parentWrapper = parent?.parentElement;

                parentWrapper?.classList[action]('filter-checkbox--active');
            });

        const checkedCheckboxes = [
            ...(document.querySelectorAll(
                `#${elementId} input[type='checkbox']:checked`,
            ) as $FixMe),
        ];

        const unCheckedCheckboxes = [
            ...(document.querySelectorAll(
                `#${elementId} input[type='checkbox']:not(:checked)`,
            ) as $FixMe),
        ];

        applyClasses(checkedCheckboxes, true);
        applyClasses(unCheckedCheckboxes, false);
    }, [elementId]);

    const handleCheckboxChange = (
        selectedOptions: string[] | string,
        { isChecked, value }: { isChecked?: boolean; value?: string },
    ) => {
        let parsedSelectedOptions = selectedOptions;

        /*
        We want to ensure that if the user selected an option with value
        of "all" (eg. Any language) we automatically uncheck any other
        checkboxes. Additionally, if the user selects a value when "all"
        is selected, we want to automatically uncheck that option.
        */
        if (value === 'all' && isChecked && parsedSelectedOptions.length > 1) {
            parsedSelectedOptions = 'all';
        } else if (value !== 'all' && isChecked) {
            parsedSelectedOptions = (selectedOptions as string[]).filter(
                (option: string) => option !== 'all',
            );
        }

        return props.onClick?.(parsedSelectedOptions);
    };

    useEffect(() => {
        /*
        check for checked checkboxes on value
        change for selected filters
        */
        setActiveClasses();
    }, [props.selectedFilters, setActiveClasses]);

    return (
        <div
            id={elementId}
            data-testid={`filter-checkbox-group--${props.name}`}
            className="filter-checkbox-group"
        >
            <CheckboxGroup
                values={props.values}
                onChange={handleCheckboxChange}
                value={
                    typeof props.selectedFilters === 'string'
                        ? [props.selectedFilters]
                        : props.selectedFilters
                }
                name={props.name}
            />
        </div>
    );
};

export default FilterCheckboxGroup;
